import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * PaywallModal - Industrial control panel aesthetic
 * Shows when user's trial has expired, blocking access to premium features
 */
function PaywallModal({ isOpen, onClose, user, onUpgrade }) {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      setTimeout(() => setIsVisible(false), 300);
    }
  }, [isOpen]);

  if (!isVisible) return null;

  // Calculate trial status
  const trialEndsAt = user?.trial_ends_at ? new Date(user.trial_ends_at) : null;
  const now = new Date();
  const isTrialActive = user?.is_trial_active ?? false;
  const isPremium = user?.is_premium ?? false;
  const canCreateData = user?.can_create_data ?? false;

  // Days calculation
  const diffMs = trialEndsAt ? trialEndsAt - now : 0;
  const daysRemaining = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  const daysExpired = Math.abs(Math.min(0, daysRemaining));

  // If premium, don't show
  if (isPremium) return null;

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      navigate('/upgrade');
    }
  };

  const handleExportData = () => {
    navigate('/account');
    if (onClose) onClose();
  };

  const handleContinue = () => {
    if (onClose) onClose();
  };

  const statusColor = isTrialActive ? 'text-amber-400' : 'text-red-500';
  const statusBg = isTrialActive ? 'bg-amber-400' : 'bg-red-500';
  const statusText = isTrialActive ? 'TRIAL ACTIVE' : 'TRIAL EXPIRED';

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Backdrop with industrial pattern */}
      <div
        className="absolute inset-0 bg-slate-900/90 backdrop-blur-sm"
        style={{
          backgroundImage: `
            repeating-linear-gradient(
              90deg,
              transparent,
              transparent 50px,
              rgba(30, 58, 95, 0.1) 50px,
              rgba(30, 58, 95, 0.1) 51px
            ),
            repeating-linear-gradient(
              0deg,
              transparent,
              transparent 50px,
              rgba(30, 58, 95, 0.1) 50px,
              rgba(30, 58, 95, 0.1) 51px
            )
          `
        }}
        onClick={isTrialActive ? handleContinue : undefined}
      />

      {/* Modal Panel */}
      <div
        className={`relative w-full max-w-lg transform transition-all duration-300 ${
          isAnimating ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        {/* Corner brackets - technical drawing style */}
        <div className="absolute -top-1 -left-1 w-6 h-6 border-t-2 border-l-2 border-orange-500" />
        <div className="absolute -top-1 -right-1 w-6 h-6 border-t-2 border-r-2 border-orange-500" />
        <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-2 border-l-2 border-orange-500" />
        <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-2 border-r-2 border-orange-500" />

        {/* Main card */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden shadow-2xl">

          {/* Header - Status Bar */}
          <div className="bg-primary px-6 py-4 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Status LED indicator */}
                <div className="relative">
                  <div className={`w-3 h-3 rounded-full ${statusBg} ${!isTrialActive ? 'animate-pulse' : ''}`} />
                  <div className={`absolute inset-0 w-3 h-3 rounded-full ${statusBg} opacity-50 ${!isTrialActive ? 'animate-ping' : ''}`} />
                </div>
                <span className={`text-xs font-bold tracking-[0.2em] ${statusColor}`}>
                  {statusText}
                </span>
              </div>
              <div className="flex items-center gap-2 text-slate-400">
                <i className="fas fa-shield-halved text-sm" />
                <span className="text-xs font-mono">LICENSE STATUS</span>
              </div>
            </div>
          </div>

          {/* Warning stripe - construction barrier style */}
          {!isTrialActive && (
            <div
              className="h-2"
              style={{
                background: 'repeating-linear-gradient(45deg, #f97316, #f97316 10px, #1e3a5f 10px, #1e3a5f 20px)'
              }}
            />
          )}

          {/* Content */}
          <div className="p-4">
            {/* Main message */}
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-700/50 border-2 border-slate-600 mb-3">
                <i className={`fas ${isTrialActive ? 'fa-clock' : 'fa-lock'} text-xl ${isTrialActive ? 'text-amber-400' : 'text-red-500'}`} />
              </div>

              <h2 className="text-xl font-black text-white mb-1 tracking-tight">
                {isTrialActive ? 'Trial Period Active' : 'Access Restricted'}
              </h2>

              <p className="text-slate-400 text-sm">
                {isTrialActive ? (
                  <>
                    <span className="text-amber-400 font-bold">{daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</span> remaining in your trial period
                  </>
                ) : (
                  <>
                    Trial ended <span className="text-red-400 font-bold">{daysExpired} day{daysExpired !== 1 ? 's' : ''} ago</span>
                  </>
                )}
              </p>
            </div>

            {/* Trial progress meter */}
            <div className="bg-slate-900/50 rounded-lg p-3 mb-4 border border-slate-700">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1 font-mono">
                <span>TRIAL USAGE</span>
                <span>{isTrialActive ? `${7 - daysRemaining}/7 DAYS` : 'EXPIRED'}</span>
              </div>
              <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${isTrialActive ? 'bg-amber-500' : 'bg-red-500'}`}
                  style={{ width: isTrialActive ? `${((7 - daysRemaining) / 7) * 100}%` : '100%' }}
                />
              </div>
            </div>

            {/* Features list - compact grid */}
            <div className="bg-slate-900/30 rounded-lg border border-slate-700 mb-4">
              <div className="px-3 py-1.5 border-b border-slate-700 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-xs font-bold text-slate-400 tracking-wider">PRO FEATURES</span>
              </div>
              <div className="p-3 grid grid-cols-2 gap-2">
                {[
                  { icon: 'fa-infinity', text: 'Unlimited orders' },
                  { icon: 'fa-file-pdf', text: 'PDF generation' },
                  { icon: 'fa-signature', text: 'Digital signatures' },
                  { icon: 'fa-envelope', text: 'Email delivery' },
                  { icon: 'fa-calendar-check', text: 'Calendar sync' },
                  { icon: 'fa-headset', text: 'Priority support' },
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <i className={`fas ${feature.icon} text-orange-500 w-4`} />
                    <span className="text-slate-300">{feature.text}</span>
                    <i className="fas fa-check text-green-500 text-[10px] ml-auto" />
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing + CTA combined */}
            <div className="flex items-center gap-3 mb-3 p-3 bg-gradient-to-r from-orange-500/10 via-orange-500/20 to-orange-500/10 rounded-lg border border-orange-500/30">
              <div className="flex items-baseline">
                <span className="text-orange-500 font-bold">$</span>
                <span className="text-white text-2xl font-black">19</span>
                <span className="text-slate-400 text-xs ml-1">/mo</span>
              </div>
              <button
                onClick={handleUpgrade}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold py-2.5 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm"
              >
                <i className="fas fa-rocket text-xs" />
                UPGRADE TO PRO
              </button>
            </div>

            {/* Secondary options */}
            {isTrialActive ? (
              <button
                onClick={handleContinue}
                className="w-full bg-slate-700/50 hover:bg-slate-700 text-slate-300 font-medium py-2 px-4 rounded-lg transition-all duration-200 border border-slate-600 text-sm"
              >
                Continue with Trial
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleExportData}
                  className="flex-1 bg-slate-700/50 hover:bg-slate-700 text-slate-300 font-medium py-2 px-3 rounded-lg transition-all duration-200 border border-slate-600 text-xs flex items-center justify-center gap-1.5"
                >
                  <i className="fas fa-download" />
                  Export Data
                </button>
                <button
                  onClick={handleContinue}
                  className="flex-1 bg-slate-700/50 hover:bg-slate-700 text-slate-300 font-medium py-2 px-3 rounded-lg transition-all duration-200 border border-slate-600 text-xs flex items-center justify-center gap-1.5"
                >
                  <i className="fas fa-eye" />
                  View Only
                </button>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default PaywallModal;
